import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AccessType, ArcHospiDto, ArcHospiStatusCode, EArcHospiActionCode, RefLanguageDto } from 'src/app/api/models';
import { eKioskError } from 'src/app/shared/models/api/constants';
import { AppState, getState } from 'src/app/state/app.state';
import { environment } from 'src/environments/environment';
import { getArcHospi } from '../components/archospi/state/archospi.selectors';
import { ArcHospiService as ArcHospiInternalService } from 'src/app/api/services/arc-hospi.service';
@Injectable({
    providedIn: "root",
})
export class ArcHospiService {
    private arcHospi$: Observable<ArcHospiDto> = this.store.pipe(select(getArcHospi));
    private currentArcHospi: ArcHospiDto;
    private subs: Subscription[] = [];

    constructor(http: HttpClient, private router: Router, private store: Store<AppState>, private archospiService: ArcHospiInternalService) {
        this.arcHospi$.subscribe(_ => this.currentArcHospi = _);
    }

    private fillArcHospi(): Partial<ArcHospiDto> {

        const objArcHospi: Partial<ArcHospiDto> = {};
        if (this.currentArcHospi != null)
            Object.assign(objArcHospi, this.currentArcHospi);

        objArcHospi.channel = environment.kioskMode ? AccessType.Kiosk : AccessType.Home;
        objArcHospi.pageContext = this.router.url;
        objArcHospi.actionCode = EArcHospiActionCode.CreatePrehospi;
        objArcHospi.status = ArcHospiStatusCode.Started;
        if (environment.kioskMode){
            this.subs.push(
                getState(this.store).subscribe(state =>{
                    objArcHospi.startMethod = state.transactionInfo.startMethod
                })
            );
        }
        return objArcHospi;
    }

    identification(cardNumber: string, insurerId: string, siteId: number, hospitalNo: string, language: RefLanguageDto): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.cardId = cardNumber;
        objArcHospi.insurerId = insurerId;

        objArcHospi.errorCode = eKioskError.STARTED;
        objArcHospi.siteId = siteId;
        objArcHospi.hospitalNo = hospitalNo;
        objArcHospi.language = language.languageCode;

        return this.archospiService.arcHospiPost({ body: objArcHospi });
    }
    agreementSigned(): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    hospitalSelected(siteId: number, hospitalNo: string): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.siteId = siteId;
        objArcHospi.hospitalNo = hospitalNo;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    admissionPlannedDateEntered(date: Date): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.functionalDate = date.toISOString();

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    admissionPlannedDateIntervalError(): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.errorCode = eKioskError.ADMISSION_DATE_INTERVAL_ERROR;
        objArcHospi.status = ArcHospiStatusCode.Error;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }
    contractBlocked(): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.errorCode = eKioskError.CONTRACT_BLOCKED;
        objArcHospi.status = ArcHospiStatusCode.Error;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }
    contractInvalid(kioskError: eKioskError): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.errorCode = kioskError;
        objArcHospi.status = ArcHospiStatusCode.Error;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }
    questionnaireSelected(questionnaireId: number): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.questionnaireId = questionnaireId;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    preHospiOverwritten(): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.status = ArcHospiStatusCode.Overwritten;
        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    preHospiComplete(hospitalizationId: number, lightColor: string, medicalFormRequired: boolean, medicalFormName: string, reasons: string): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.lightSummaryPrintedYn = "Y";
        objArcHospi.light = lightColor;
        objArcHospi.medForm = medicalFormRequired ? "Y" : "N";
        objArcHospi.medFormName = medicalFormName;

        objArcHospi.hospitalizationId = hospitalizationId;
        objArcHospi.errorCode = eKioskError.OK;
        objArcHospi.status = ArcHospiStatusCode.Created;
        objArcHospi.reasons = reasons;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    medicalFormPrinted(): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.medFormPrintedYn = "Y";

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    stopWithError(errorCode: eKioskError): Observable<ArcHospiDto> {
        const objArcHospi = this.fillArcHospi();

        objArcHospi.errorCode = (errorCode == null ? eKioskError.TECHNICAL_ERROR : errorCode);
        objArcHospi.status = ArcHospiStatusCode.Error;

        return this.archospiService.arcHospiPut({ body: objArcHospi });
    }

    ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}
}
